
import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface LoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  setIsResetMode: (isResetMode: boolean) => void;
  sendMagicLink: (e: React.FormEvent) => Promise<void>;
}

const LoginForm = ({ 
  email, 
  setEmail, 
  password, 
  setPassword, 
  loading, 
  setLoading,
  setIsResetMode,
  sendMagicLink
}: LoginFormProps) => {
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast.error("Login failed", {
          description: error.message,
        });
        throw error;
      }

      toast.success('Login successful', {
        description: 'Welcome back!'
      });
      // On successful login, the useEffect in Auth.tsx will redirect to dashboard
    } catch (error: any) {
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="login-email">Email</Label>
        <Input 
          id="login-email" 
          value={email} 
          onChange={(e) => setEmail(e.target.value)} 
          type="email" 
          placeholder="your@email.com"
          required
        />
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between">
          <Label htmlFor="login-password">Password</Label>
          <button 
            type="button"
            className="text-sm text-mint hover:underline"
            onClick={() => setIsResetMode(true)}
          >
            Forgot password?
          </button>
        </div>
        <Input 
          id="login-password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          type="password" 
          required
        />
      </div>
      
      <Button type="submit" className="w-full bg-mint text-forest hover:bg-mint/90" disabled={loading}>
        {loading ? 'Signing In...' : 'Sign In'}
      </Button>
      
      <div className="text-center">
        <span className="text-sm text-gray-500">Or</span>
      </div>
      
      <Button 
        type="button" 
        variant="outline" 
        className="w-full border-mint text-mint hover:bg-mint/10"
        onClick={sendMagicLink}
        disabled={loading}
      >
        Sign In with Magic Link
      </Button>
    </form>
  );
};

export default LoginForm;
